import React, { useState, useEffect } from "react";
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  GroupAdd as GroupAddIcon,
  Security as PermissionsIcon,
  People as PeopleIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Shield as SystemIcon,
} from "@mui/icons-material";
import {
  groupsService,
  type Group as GroupType,
} from "@/services/groupsService";

/**
 * Tabla de grupos con CRUD completo
 *
 * REGLAS APLICADAS:
 * - Solo <Stack> para layout (NO Grid)
 * - Componentes con size="small"
 * - dense={true} en tabla
 * - Máximo 500 líneas
 * - Usa groupsService con autenticación automática JWT
 * - Loading dentro de la tabla (NO reemplaza toda la tabla)
 * - CircularProgress size={48} centrado
 */

type Group = GroupType;

interface GroupTableProps {
  onEdit: (group: Group) => void;
  onDelete: (groupId: string) => void;
  onAssignPermissions: (group: Group) => void;
  onViewUsers: (group: Group) => void;
  onToggleStatus: (groupId: string, newStatus: boolean) => void;
}

export function GroupTable({
  onEdit,
  onDelete,
  onAssignPermissions,
  onViewUsers,
  onToggleStatus,
}: GroupTableProps) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    groupId: string;
    groupName: string;
    isSystem: boolean;
  }>({
    open: false,
    groupId: "",
    groupName: "",
    isSystem: false,
  });

  // Cargar grupos
  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      setLoading(true);
      setError(null);

      // Usar servicio con autenticación automática JWT
      const data = await groupsService.getGroups();
      setGroups(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error desconocido");
      console.error("Error cargando grupos:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (group: Group) => {
    setDeleteDialog({
      open: true,
      groupId: group.id,
      groupName: group.nombre,
      isSystem: group.es_sistema,
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await onDelete(deleteDialog.groupId);
      setDeleteDialog({
        open: false,
        groupId: "",
        groupName: "",
        isSystem: false,
      });
      await loadGroups(); // Recargar lista
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error eliminando grupo");
    }
  };

  const handleToggleStatus = async (
    groupId: string,
    currentStatus: boolean,
  ) => {
    try {
      await onToggleStatus(groupId, !currentStatus);
      await loadGroups(); // Recargar lista
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Error cambiando estado del grupo",
      );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  return (
    <Stack spacing={2}>
      {error && (
        <Alert severity="error">
          {error}
          <Button size="small" onClick={loadGroups} sx={{ ml: 1 }}>
            Reintentar
          </Button>
        </Alert>
      )}

      <TableContainer component={Paper} elevation={1}>
        <Table size="small" aria-label="tabla de grupos">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell align="center">Tipo</TableCell>
              <TableCell align="center">Estado</TableCell>
              <TableCell>Fecha Creación</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
                    <CircularProgress size={48} />
                    <Typography variant="body2" color="text.secondary">
                      Cargando grupos...
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : groups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
                    <GroupAddIcon
                      sx={{ fontSize: 48, color: "text.disabled" }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      No hay grupos disponibles
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Los grupos aparecerán aquí una vez que sean creados
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : (
              groups.map((group) => (
                <TableRow key={group.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{group.nombre}</Typography>
                      {group.es_sistema && (
                        <Tooltip title="Grupo del sistema">
                          <SystemIcon color="warning" fontSize="small" />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {group.descripcion || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      icon={group.es_sistema ? <SystemIcon /> : <PeopleIcon />}
                      label={group.es_sistema ? "Sistema" : "Personalizado"}
                      color={group.es_sistema ? "warning" : "default"}
                      variant={group.es_sistema ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      icon={group.activo ? <ActiveIcon /> : <InactiveIcon />}
                      label={group.activo ? "Activo" : "Inactivo"}
                      color={group.activo ? "success" : "default"}
                      variant={group.activo ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell>{formatDate(group.fecha_creacion)}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Editar grupo">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onEdit(group)}
                            color="primary"
                            disabled={group.es_sistema}
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="Asignar permisos">
                        <IconButton
                          size="small"
                          onClick={() => onAssignPermissions(group)}
                          color="secondary"
                        >
                          <PermissionsIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Ver usuarios">
                        <IconButton
                          size="small"
                          onClick={() => onViewUsers(group)}
                          color="info"
                        >
                          <PeopleIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={group.activo ? "Desactivar" : "Activar"}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleToggleStatus(group.id, group.activo)
                            }
                            color={group.activo ? "warning" : "success"}
                            disabled={group.es_sistema}
                          >
                            {group.activo ? <InactiveIcon /> : <ActiveIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip
                        title={
                          group.es_sistema
                            ? "No se puede eliminar (Sistema)"
                            : "Eliminar grupo"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(group)}
                            color="error"
                            disabled={group.es_sistema}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo de confirmación para eliminar */}
      <Dialog
        open={deleteDialog.open}
        onClose={() =>
          setDeleteDialog({
            open: false,
            groupId: "",
            groupName: "",
            isSystem: false,
          })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            ¿Estás seguro de que deseas eliminar el grupo{" "}
            <strong>{deleteDialog.groupName}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta acción no se puede deshacer y eliminará todas las relaciones
            con usuarios.
          </Typography>
          {deleteDialog.isSystem && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Este es un grupo del sistema y no puede ser eliminado.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            onClick={() =>
              setDeleteDialog({
                open: false,
                groupId: "",
                groupName: "",
                isSystem: false,
              })
            }
          >
            Cancelar
          </Button>
          <Button
            size="small"
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteDialog.isSystem}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
